import { useEffect, useMemo, useState } from "react";
import { Col, Modal, Row } from "react-bootstrap";
import { connect } from "react-redux";
import {
  buttonNameConstants,
  buttonTypeConstants,
  EMPTY_STRING,
  collectPosFields as initialCollectPosFields,
  SHOPIFY_OPTIONS_LIST,
  shopifyConstants,
  shopifyPredefinedColumns,
  shopifySelectionOptions,
  toastType,
  WEIGHT_UNIT_OPTIONS,
} from "../../../../constants";
import { shopifyActions } from "../../../../redux/actions";
import { ShopifyTags } from "../../../../shared/components";
import Button from "../../../../shared/components/Buttons/Button";
import SimpleCheckbox from "../../../../shared/components/checkBox/SimpleCheckbox";
import ComboBoxField from "../../../../shared/components/comboBoxField/ComboBoxField";
import SimpleNumberField from "../../../../shared/components/inputNumberField/SimpleNumberField";
import ShopifyCompareAtPrice from "../../../../shared/components/inputTextArea/ShopifyCompareAtPrice";
import TextAreaWithInsertVariable from "../../../../shared/components/inputTextArea/TextAreaWithInsertVariable";
import SimpleInputTextField from "../../../../shared/components/inputTextField/SimpleInputTextField";
import SelectDropDown from "../../../../shared/components/selectDropdown/SelectDropdown";
import {
  customToast,
  handleCleanShopifyComboBoxCustomValue,
  handleConvertStringBooleanIntoBoolean,
  isSpinnerEnabled,
} from "../../../../shared/utility";

const ShopifyPresetsMappingModal = ({
  shopify,
  currentStore,
  spinnerArray,
  handleSaveClick,
  initialMappings = {},
  isShopifyMappingModal,
  getShopifyProductTags,
  getShopifyThemeTemplates,
  toggleShopifyMappingModal,
  isOnlineQuantity,
  isShowInStoreOnlineQuantityCheck,
}) => {
  //--------Shopify Tags
  const { shopifyTags, shopifyThemeTemplates } = shopify;

  const [fieldMappings, setFieldMappings] = useState(initialMappings);
  const [collectFields, setCollectFields] = useState(initialCollectPosFields);

  const requiredFields = useMemo(
    () => shopifyPredefinedColumns.filter((field) => field.required),
    []
  );

  const handleHideModal = () => {
    toggleShopifyMappingModal();
  };

  const handleMappingChange = (fieldName, selected) => {
    const newValue = { [fieldName]: selected?.value ?? "" };
    setFieldMappings((prev) => {
      return { ...prev, ...newValue };
    });
  };

  const handleAddNewValueClick = (inputValue, fieldName) => {
    // Add a custom value idetifier constant to the input value
    const newFieldValue = String(inputValue).includes(
      shopifyConstants.COMBO_BOX_INPUT_VALUE
    )
      ? inputValue
      : shopifyConstants.COMBO_BOX_INPUT_VALUE + inputValue;
    if (inputValue) {
      const newOption = { label: inputValue, value: newFieldValue };

      setCollectFields((prev) => {
        const exists = prev.some((field) => field.value === newFieldValue);
        return exists ? prev : [...prev, newOption];
      });

      setFieldMappings((prev) => ({
        ...prev,
        [fieldName]: newOption.value,
      }));
    }
  };

  const validateMappings = () => {
    const isEmpty = (val) => val === undefined || val === null || val === "";
    return requiredFields.filter((field) => isEmpty(fieldMappings[field.name]));
  };

  const handleSave = () => {
    const missingFields = validateMappings();
    if (missingFields.length > 0) {
      customToast(
        `Please map all required fields: ${missingFields
          .map((m) => m.label)
          .join(", ")}`,
        toastType.ERROR
      );
      return;
    }

    for (let i = 1; i < SHOPIFY_OPTIONS_LIST.length; i++) {
      const option = SHOPIFY_OPTIONS_LIST[i];
      const nameValue = fieldMappings[option.name];
      const valueValue = fieldMappings[option.value];
      if (nameValue && !valueValue) {
        customToast(
          `Please map a CollectPos field for Option ${i + 1} Values`,
          toastType.ERROR
        );
        return;
      }
    }
    fieldMappings.isOnlineQuantity = handleConvertStringBooleanIntoBoolean(
      fieldMappings.isOnlineQuantity
    );

    handleSaveClick(fieldMappings);
  };

  const renderField = (field) => {
    switch (field.type) {
      case shopifyConstants.SHOPIFY_COMBO_FIELD:
        return (
          <ComboBoxField
            options={collectFields}
            handleChange={(opt) => handleMappingChange(field.name, opt)}
            selectedValue={
              fieldMappings[field.name]
                ? collectFields.find(
                    (c) => c.value === fieldMappings[field.name]
                  )
                : null
            }
            placeHolder="Select..."
            handleAddClick={handleAddNewValueClick}
            fieldName={field.name}
            isDisabled={field.isDisabled}
            isClearable={SHOPIFY_OPTIONS_LIST.map((opt) => opt.value).includes(
              field.name
            )}
          />
        );
      case shopifyConstants.SHOPIFY_SELECTION_FIELD:
        const value = String(fieldMappings[field.name]);
        return (
          <SelectDropDown
            menuPlacement="auto"
            options={shopifySelectionOptions}
            placeHolder="Select"
            handleChange={(opt) => handleMappingChange(field.name, opt)}
            menuPortalTarget={""}
            value={shopifySelectionOptions.find(
              (c) => String(c.value) === value
            )}
            isDisabled={field.isDisabled}
          />
        );
      case shopifyConstants.SHOPIFY_TEXT_FIELD:
        return (
          <SimpleInputTextField
            value={fieldMappings[field.name] || ""}
            onChange={(e) =>
              handleMappingChange(field.name, { value: e.target.value })
            }
            isDisabled={field.isDisabled}
            placeHolder={field.placeHolder}
          />
        );
      case shopifyConstants.SHOPIFY_NOT_EDITABLE_FIELD:
        return (
          <SimpleInputTextField
            value={field.placeHolder}
            isDisabled={field.isDisabled}
          />
        );
      case shopifyConstants.OPTIONS_NAME:
        return (
          <div className="d-flex flex-column gap-2">
            {field.optionFields.map((opt) => {
              return (
                <SimpleInputTextField
                  value={
                    opt.name === SHOPIFY_OPTIONS_LIST[0].name
                      ? "Condition"
                      : fieldMappings[opt.name]
                  }
                  isDisabled={opt.name === SHOPIFY_OPTIONS_LIST[0].name}
                  onChange={(e) => {
                    handleMappingChange(opt.name, { value: e.target.value });
                  }}
                />
              );
            })}
          </div>
        );
      case shopifyConstants.SHOPIFY_NUMERIC_FIELD:
        return (
          <SimpleNumberField
            value={fieldMappings[field.name] || ""}
            onChange={(e) => handleMappingChange(field.name, { value: e })}
            isDisabled={field.isDisabled}
            placeHolder={field.placeHolder}
          />
        );
      case shopifyConstants.SHOPIFY_TEXT_AREA_WITH_INSERT_VARIABLE_FIELD:
        return (
          <TextAreaWithInsertVariable
            isDisabled={field.isDisabled}
            placeHolder={field.placeHolder}
            variableOptions={initialCollectPosFields}
            value={fieldMappings[field.name] || EMPTY_STRING}
            onChange={(e) => handleMappingChange(field.name, { value: e })}
          />
        );
      case shopifyConstants.SHOPIFY_WEIGHT_UNIT_FIELD:
        return (
          <SelectDropDown
            options={WEIGHT_UNIT_OPTIONS}
            handleChange={(opt) => handleMappingChange(field.name, opt)}
            defaultPageSize={
              fieldMappings[field.name]
                ? WEIGHT_UNIT_OPTIONS.find(
                    (c) => c.value === fieldMappings[field.name]
                  )
                : EMPTY_STRING
            }
            placeHolder="Select Unit..."
            isDisabled={field.isDisabled}
            menuPortalTarget=""
            menuPlacement="auto"
          />
        );
      case shopifyConstants.SHOPIFY_TAGS_FIELD:
        return (
          <ShopifyTags
            shopifyTags={shopifyTags}
            isDisabled={field.isDisabled}
            placeHolder={field.placeHolder}
            variableOptions={initialCollectPosFields}
            value={fieldMappings[field.name] || EMPTY_STRING}
            onChange={(e) => handleMappingChange(field.name, { value: e })}
          />
        );
      case shopifyConstants.SHOPIFY_INVENTORY_POLICY_FIELD:
        return (
          <SelectDropDown
            options={shopifyConstants.SHOPIFY_INVENTORY_POLICY_OPTIONS}
            handleChange={(opt) => handleMappingChange(field.name, opt)}
            defaultPageSize={
              fieldMappings[field.name]
                ? shopifyConstants.SHOPIFY_INVENTORY_POLICY_OPTIONS.find(
                    (c) => c.value === fieldMappings[field.name]
                  )
                : shopifyConstants.SHOPIFY_INVENTORY_POLICY_OPTIONS[0]
            }
            placeHolder="Select Unit..."
            isDisabled={field.isDisabled}
            menuPortalTarget=""
            menuPlacement="auto"
          />
        );
      case shopifyConstants.SHOPIFY_COMPARE_AT_PRICE_FIELD:
        return (
          <ShopifyCompareAtPrice
            field={field}
            fieldMappings={fieldMappings}
            handleMappingChange={handleMappingChange}
          />
        );
      case shopifyConstants.SHOPIFY_THEME_FIELD:
        const storedValue = fieldMappings[field.name] || EMPTY_STRING;
        // Find matching template object
        const selectedOption =
          shopifyThemeTemplates.find((t) => t.value === storedValue) || null;
        // If no matching label is found but there's a storedValue, create a fallback
        const displayValue =
          selectedOption ||
          (storedValue
            ? {
                label:
                  storedValue.charAt(0).toUpperCase() + storedValue.slice(1),
                value: storedValue,
              }
            : null);
        return (
          <SelectDropDown
            options={shopifyThemeTemplates}
            handleChange={(opt) => handleMappingChange(field.name, opt)}
            defaultPageSize={
              shopifyThemeTemplates.find(
                (t) => t.value === fieldMappings[field.name]
              ) || null
            }
            placeHolder="Select Theme Template..."
            value={displayValue}
            isDisabled={field.isDisabled}
            menuPortalTarget=""
            menuPlacement="auto"
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    setFieldMappings((prev) => ({
      ...prev,
      [SHOPIFY_OPTIONS_LIST[0].value]: "type",
      [SHOPIFY_OPTIONS_LIST[0].name]: "Condition",
    }));
  }, [collectFields]);

  useEffect(() => {
    // Only add CollectPos fields from initialMappings that are mapped to a shopifyPredefinedColumns
    // where type is SHOPIFY_COMBO_FIELD
    const comboFieldNames = shopifyPredefinedColumns
      .filter((col) => col.type === shopifyConstants.SHOPIFY_COMBO_FIELD)
      .map((col) => col.name);

    const extraFields = comboFieldNames
      .map((name) => initialMappings[name])
      .filter(
        (val) => val && !collectFields.some((field) => field.value === val)
      )
      .map((val) => ({
        label: handleCleanShopifyComboBoxCustomValue(val),
        value: val,
      }));

    if (extraFields.length > 0) {
      setCollectFields((prev) => {
        const existingValues = new Set(prev.map((f) => f.value));
        const newFields = extraFields.filter(
          (f) => !existingValues.has(f.value)
        );
        return [...prev, ...newFields];
      });
    }
    // Only run when initialMappings or collectFields change
    // eslint-disable-next-line
  }, [initialMappings, collectFields]);

  useEffect(() => {
    getShopifyProductTags(currentStore?.id);
    if (isShowInStoreOnlineQuantityCheck) {
      handleMappingChange(shopifyConstants.SEPERATE_QUANTITY, {
        value: isOnlineQuantity,
      });
    }
    getShopifyThemeTemplates(currentStore?.id);
    //eslint-disable-next-line
  }, []);

  const handleSeperateOnlineQuantityCheckbox = (name, checked) => {
    handleMappingChange(name, { value: checked });
  };

  return (
    <Modal
      centered
      show={isShopifyMappingModal}
      onHide={toggleShopifyMappingModal}
      size="lg"
      backdrop="static"
      className="add-shopify-preset-mapping-modal"
      aria-labelledby="shopify-mapping-modal-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="shopify-mapping-modal-title">
          Shopify Presets
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-between">
          <p>* Required Fields</p>

          {isShowInStoreOnlineQuantityCheck && (
            <SimpleCheckbox
              name={shopifyConstants.SEPERATE_QUANTITY}
              label={buttonNameConstants.SEPARATE_QUANTITY}
              onChange={handleSeperateOnlineQuantityCheckbox}
              checked={isOnlineQuantity}
              isLabelLeft={true}
            />
          )}
        </div>
        <div className="column-mapping-table-wrapper container-scrollbar">
          <Row className="m-0 py-3 border-bottom px-0 import-inventory-column-header-wrapper">
            <Col md={4}>
              <span className="import-inventory-columns-heading">
                Shopify Fields
              </span>
            </Col>
            <Col md={8}>
              <span className="import-inventory-columns-heading">
                CollectPos Fields
              </span>
            </Col>
          </Row>
          <div className="shopify-columns-modal container-scrollbar pb-3">
            {shopifyPredefinedColumns.map((field, index) => (
              <Row
                key={`${field.name}-${index}`}
                className="m-0 py-2 border-bottom px-0"
              >
                <Col md={4} className="d-flex align-items-center">
                  <span className="predefinded-column-label">
                    {field.label}
                    {field.required && " *"}
                  </span>
                </Col>
                <Col md={8}>{renderField(field)}</Col>
              </Row>
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Row className="w-100">
          <Col md={8}></Col>
          <Col md={2}>
            <Button
              type="button"
              label={buttonNameConstants.CANCEL}
              buttonType={buttonTypeConstants.GHOST_BUTTON}
              handleClick={handleHideModal}
              isDisabled={isSpinnerEnabled(
                spinnerArray,
                shopifyConstants.UPDATE_GLOBAL_SHOPIFY_PRESET
              )}
            />
          </Col>
          <Col md={2}>
            <Button
              type="button"
              label={buttonNameConstants.SAVE_MAPPINGS}
              handleClick={handleSave}
              isDisabled={isSpinnerEnabled(
                spinnerArray,
                shopifyConstants.UPDATE_GLOBAL_SHOPIFY_PRESET
              )}
            />
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  shopify: state.shopify,
  currentStore: state.store.currentStore,
  spinnerArray: state.system.spinnerArray,
});
//-------Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getShopifyProductTags: (storeId) =>
    dispatch(shopifyActions.getShopifyProductTags(storeId)),
  getShopifyThemeTemplates: (storeId) =>
    dispatch(shopifyActions.getShopifyThemeTemplates(storeId)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ShopifyPresetsMappingModal);
